import { useModal } from "@ebay/nice-modal-react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import useTypedContext from "hooks/useTypedContext";
import { ConfigElement, ConfigInput, ConfigType } from "types/generated";
import { getDrawerFormFix } from "utils/css";

import FormDescriptionField from "../FormItems/DescriptionField";
import FormIsSecretField from "../FormItems/IsSecretField";
import FormNameField from "../FormItems/NameField";
import FormValueField from "../FormItems/ValueField";

type DrawerEnvVariableProps = {
  variable?: ConfigElement;
  onSubmit: (data: ConfigInput) => Promise<string | undefined>;
  existingNames?: string[];
};

type EnvVariableFormValues = {
  id: string;
  value: string;
  description: string;
  writeOnly: boolean;
};

const envVariableFormDrawer = createDrawer(
  ({ variable, onSubmit, existingNames }: DrawerEnvVariableProps) => {
    const isEdit = !!variable;
    const drawer = useModal();
    const [loading, setLoading] = useState(false);
    const { reportSuccess } = useTypedContext(FlashContext);

  const builderForm = useForm<EnvVariableFormValues>({
    defaultValues: {
      id: variable?.id ?? "",
      value: variable?.value ?? "",
      description: variable?.description ?? "",
      writeOnly: variable?.writeOnly ?? true,
    },
    shouldFocusError: true,
    mode: "onChange",
  });

  const closeDrawer = () => {
    drawer.resolve();
    drawer.hide();
  };

  const {
    handleSubmit,
    formState: { isDirty },
    watch,
  } = builderForm;

  const submitHandler = async (formData: EnvVariableFormValues) => {
    try {
      setLoading(true);
      const id = await onSubmit({
        ...formData,
        type: ConfigType.EnvironmentVariable,
        fileMode: null,
      });
      if (id) {
        reportSuccess({
          message: `Environment variable was ${isEdit ? "updated" : "created"} successfully`,
        });
        closeDrawer();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...builderForm}>
        <form onSubmit={handleSubmit(submitHandler)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle
              title={isEdit ? "Edit environment variable" : "New environment variable"}
            />
            <DrawerCloseIcon />
          </DrawerHeader>

          <DrawerBody gap="x-large">
            <FormNameField
              name="id"
              disabled={isEdit}
              existingNames={isEdit ? undefined : existingNames}
            />
            <FormValueField name="value" isSecret={watch("writeOnly")} />
            <FormIsSecretField name="writeOnly" />
            <FormDescriptionField name="description" />
          </DrawerBody>
          <DrawerFooter>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button variant="primary" type="submit" disabled={loading}>
                {isEdit ? "Save" : "Create"}
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showEnvVariableFormDrawer = createDrawerTrigger(envVariableFormDrawer);
